import React, { useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  defaultDropAnimation,
  useSensor,
  useSensors,
  MeasuringStrategy,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DotsSixVertical, CaretRight, Plus, PencilSimple, Trash, ImageSquare } from "@phosphor-icons/react";
import { Badge, Tooltip } from "../../components/ui";

export const MAX_DEPTH = 3; // depths 0..2
const INDENT = 26;

/* ============================ tree utilities ============================ */

const mapCollapsed = (items, collapsed) =>
  (items || []).map((it) => ({
    ...it,
    collapsed: !!collapsed[it.id],
    children: it.children ? mapCollapsed(it.children, collapsed) : [],
  }));

function flatten(items, parentId = null, depth = 0) {
  return (items || []).reduce((acc, item, index) => {
    return [
      ...acc,
      { id: item.id, parentId, depth, index, collapsed: item.collapsed, data: item, children: item.children || [] },
      ...flatten(item.children || [], item.id, depth + 1),
    ];
  }, []);
}

function removeChildrenOf(items, ids) {
  const exclude = [...ids];
  return items.filter((item) => {
    if (item.parentId != null && exclude.includes(item.parentId)) {
      if (item.children?.length) exclude.push(item.id);
      return false;
    }
    return true;
  });
}

function buildTree(flattened) {
  const root = { id: "root", children: [] };
  const nodes = { root };
  const items = flattened.map((f) => ({ ...f.data, id: f.id, children: [] }));
  flattened.forEach((f, i) => {
    const node = items[i];
    const parentId = f.parentId ?? "root";
    const parent = nodes[parentId] || root;
    nodes[f.id] = node;
    parent.children.push(node);
  });
  return root.children;
}

function countDescendants(items = []) {
  return items.reduce((acc, i) => acc + 1 + countDescendants(i.children), 0);
}
function findDeep(items, id) {
  for (const item of items || []) {
    if (item.id === id) return item;
    const f = findDeep(item.children, id);
    if (f) return f;
  }
  return undefined;
}

function getProjection(items, activeId, overId, dragOffset) {
  const overIndex = items.findIndex(({ id }) => id === overId);
  const activeIndex = items.findIndex(({ id }) => id === activeId);
  const activeItem = items[activeIndex];
  const newItems = arrayMove(items, activeIndex, overIndex);
  const prev = newItems[overIndex - 1];
  const next = newItems[overIndex + 1];

  const dragDepth = Math.round(dragOffset / INDENT);
  const projected = activeItem.depth + dragDepth;
  const maxDepth = prev ? Math.min(prev.depth + 1, MAX_DEPTH - 1) : 0;
  const minDepth = next ? next.depth : 0;

  let depth = projected;
  if (projected >= maxDepth) depth = maxDepth;
  else if (projected < minDepth) depth = minDepth;
  depth = Math.min(Math.max(depth, 0), MAX_DEPTH - 1);

  const getParentId = () => {
    if (depth === 0 || !prev) return null;
    if (depth === prev.depth) return prev.parentId;
    if (depth > prev.depth) return prev.id;
    return (
      newItems
        .slice(0, overIndex)
        .reverse()
        .find((item) => item.depth === depth)?.parentId ?? null
    );
  };

  return { depth, parentId: getParentId() };
}

/* payload of { id, parent_id, position } from a flattened, ordered list */
function toReorderPayload(flattened) {
  const tree = buildTree(flattened);
  const out = [];
  const walk = (nodes, parentId) =>
    (nodes || []).forEach((n, i) => {
      out.push({ id: n.id, parent_id: parentId, position: i });
      walk(n.children, n.id);
    });
  walk(tree, null);
  return out;
}

/* ============================ row ============================ */

export function CategoryRow({
  value,
  depth,
  clone = false,
  ghost = false,
  childCount,
  hasChildren = false,
  collapsed = false,
  pathLabel,
  onCollapse,
  onEdit,
  onDelete,
  onAddChild,
  canManage = {},
  handleProps,
}) {
  return (
    <div style={{ paddingLeft: clone ? 0 : depth * INDENT }} className={clone ? "" : "mb-1.5"}>
      <div
        className={[
          "group flex items-center gap-2 rounded-xl border bg-surface px-2 py-2 transition",
          clone ? "w-[440px] border-accent/40 shadow-pop" : "border-line hover:border-line-strong",
          ghost ? "opacity-40" : "",
        ].join(" ")}
      >
        {/* drag handle */}
        {handleProps !== null && (
          <button
            {...(handleProps || {})}
            className="grid h-7 w-7 shrink-0 cursor-grab touch-none place-items-center rounded-lg text-faint transition hover:bg-surface-2 hover:text-muted active:cursor-grabbing"
            aria-label="Drag to reorder"
            tabIndex={-1}
          >
            <DotsSixVertical size={16} weight="bold" />
          </button>
        )}

        {/* expand / collapse */}
        <button
          type="button"
          onClick={onCollapse}
          className={`grid h-6 w-6 shrink-0 place-items-center rounded ${hasChildren ? "text-muted hover:bg-surface-2" : "invisible"}`}
          aria-label="Toggle"
        >
          <CaretRight size={13} weight="bold" className={`transition ${collapsed ? "" : "rotate-90"}`} />
        </button>

        {/* image */}
        {value.image ? (
          <img src={value.image} alt="" className="h-8 w-8 shrink-0 rounded-lg border border-line object-cover" />
        ) : (
          <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-line bg-surface-2 text-faint">
            <ImageSquare size={15} />
          </div>
        )}

        {/* name + meta */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-ink">{value.name}</span>
            {value.featured ? <Badge tone="accent">Featured</Badge> : null}
            {clone && childCount > 1 ? <Badge tone="neutral">{childCount}</Badge> : null}
          </div>
          {!clone && (pathLabel || value.slug) ? (
            <span className="truncate text-xs text-muted">{pathLabel || value.slug}</span>
          ) : null}
        </div>

        {/* actions */}
        {!clone && (
          <div className="flex items-center gap-0.5 opacity-0 transition group-hover:opacity-100">
            {onAddChild && canManage.create && (
              <Tooltip label="Add subcategory">
                <button onClick={onAddChild} className="grid h-7 w-7 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink">
                  <Plus size={15} />
                </button>
              </Tooltip>
            )}
            {canManage.update && (
              <Tooltip label="Edit">
                <button onClick={onEdit} className="grid h-7 w-7 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink">
                  <PencilSimple size={15} />
                </button>
              </Tooltip>
            )}
            {canManage.delete && (
              <Tooltip label="Delete">
                <button onClick={onDelete} className="grid h-7 w-7 place-items-center rounded-lg text-muted transition hover:bg-red-500/10 hover:text-red-600">
                  <Trash size={15} />
                </button>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SortableRow({ id, depth, ...rest }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Translate.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style}>
      <CategoryRow depth={depth} ghost={isDragging} handleProps={{ ...attributes, ...listeners }} {...rest} />
    </div>
  );
}

/* ============================ tree (DnD) ============================ */

export default function CategoryTree({ items, onChange, onPersist, onEdit, onDelete, onAddChild, canManage }) {
  const [collapsed, setCollapsed] = useState({});
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [offsetLeft, setOffsetLeft] = useState(0);

  const withCollapsed = useMemo(() => mapCollapsed(items, collapsed), [items, collapsed]);

  const flattenedItems = useMemo(() => {
    const flat = flatten(withCollapsed);
    const collapsedParents = flat.reduce(
      (acc, item) => (item.collapsed && item.children.length ? [...acc, item.id] : acc),
      []
    );
    const excluded = activeId != null ? [activeId, ...collapsedParents] : collapsedParents;
    return removeChildrenOf(flat, excluded);
  }, [withCollapsed, activeId]);

  const projected =
    activeId != null && overId != null ? getProjection(flattenedItems, activeId, overId, offsetLeft) : null;

  const sortedIds = useMemo(() => flattenedItems.map((i) => i.id), [flattenedItems]);
  const activeItem = activeId != null ? flattenedItems.find(({ id }) => id === activeId) : null;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const resetState = () => {
    setActiveId(null);
    setOverId(null);
    setOffsetLeft(0);
    document.body.style.setProperty("cursor", "");
  };

  const handleDragEnd = ({ active, over }) => {
    resetState();
    if (!projected || !over) return;
    const { depth, parentId } = projected;
    const clonedFlat = flatten(mapCollapsed(items, collapsed));
    const overIndex = clonedFlat.findIndex(({ id }) => id === over.id);
    const activeIndex = clonedFlat.findIndex(({ id }) => id === active.id);
    if (activeIndex === -1 || overIndex === -1) return;
    clonedFlat[activeIndex] = { ...clonedFlat[activeIndex], depth, parentId };
    const sorted = arrayMove(clonedFlat, activeIndex, overIndex);
    onChange?.(buildTree(sorted));
    onPersist?.(toReorderPayload(sorted));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={({ active }) => {
        setActiveId(active.id);
        setOverId(active.id);
        document.body.style.setProperty("cursor", "grabbing");
      }}
      onDragMove={({ delta }) => setOffsetLeft(delta.x)}
      onDragOver={({ over }) => setOverId(over?.id ?? null)}
      onDragEnd={handleDragEnd}
      onDragCancel={resetState}
    >
      <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
        <div className="select-none">
          {flattenedItems.map((item) => (
            <SortableRow
              key={item.id}
              id={item.id}
              value={item.data}
              depth={item.id === activeId && projected ? projected.depth : item.depth}
              hasChildren={item.children.length > 0}
              collapsed={!!item.collapsed && item.children.length > 0}
              onCollapse={item.children.length ? () => setCollapsed((c) => ({ ...c, [item.id]: !c[item.id] })) : undefined}
              onEdit={() => onEdit?.(item.data)}
              onDelete={() => onDelete?.(item.data)}
              onAddChild={item.depth < MAX_DEPTH - 1 ? () => onAddChild?.(item.id) : undefined}
              canManage={canManage}
            />
          ))}
        </div>
      </SortableContext>

      <DragOverlay dropAnimation={defaultDropAnimation}>
        {activeId != null && activeItem ? (
          <CategoryRow
            value={activeItem.data}
            depth={0}
            clone
            handleProps={null}
            childCount={countDescendants(findDeep(items, activeId)?.children) + 1}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
